import { useRef } from 'react'
import { useFormTemplate, usePageHandler, useMergeState } from './'
import { getField, addFormTemplateRow } from '../components/FormControls/common'

/**
 * @function useFormPageHandler
 * @description 
 * A react hook for executing api calls for forms. It will take a single api promise
 * or an array of api promises. It will call them in parallel after the first render.
 * It will wait for the call(s) and then call the supplied callback function with the response.
 * The busy cursor and error messages are handled by uiApiCalls.
 * The purpose of this hook allows the dev to not worry about async or await.
 * @param {object} useFormPageHandlerParams object parameters to usePageHandler.
 * @param {Array<object>} useFormPageHandlerParams.formTemplate url string or array of urls or promise based api call.
 * @param {string | string[] | Promise<any>} useFormPageHandlerParams.service url string or array of urls or promise based api call.
 * @param {Function} useFormPageHandlerParams.getCallback This function will be called when the data is available as a parameter to the function.
 * @param {boolean} useFormPageHandlerParams.allowBusy Indicates whether to use the busy cursor.
 * @param {boolean} useFormPageHandlerParams.allowErrorMsg Indicates whether to display error messages.
 * @param {Function} useFormPageHandlerParams.errorMessageFunc Error message display function.
 * @param {Function} useFormPageHandlerParams.busyOverride ref to Set busy/clear function override.
 * @param {Array} useFormPageHandlerParams.arrKeyInBackendObject Jey to the array of data in the backend.
 * @param {Array} useFormPageHandlerParams.rowFieldTemplates Array of fieldtemplates for each row. These are the column descriptions in a table.
 * @param {Array} useFormPageHandlerParams.rowComponents Array of row component for each row. These are the UI component columns in a table.
 * @returns {Array} Returns an array of refs, the ndata retrieved from the api call(s) and a stater flag indicated that the data has been retrieved.
 */
const useFormPageHandler = ({ 
  formTemplate, 
  service, 
  getCallback, 
  allowBusy, 
  allowErrorMsg, 
  errorMessageFunc, 
  busyOverride, 
  arrKeyInBackendObject, 
  rowFieldTemplates, 
  rowComponents
}) => {
  const rows = useRef([])
  const rowsArrRefs = useRef([])
  const [state, setState] = useMergeState({
    data: {},
    isLoaded: false
  })
  const arrRefs = useFormTemplate(formTemplate)
  
  const handleData = (data = {}) => {
    if (typeof getCallback === 'function') {
      data = getCallback(data)
    }
    if (arrKeyInBackendObject !== undefined) {
      const arr = getField(data, arrKeyInBackendObject)
      if (Array.isArray(arr)) {
        for (let i = 0; i < arr.length; ++i) {
          const { row, rowArrRefs } = addFormTemplateRow({ data, rowFieldTemplates, rowComponents, arrRefs, arrKeyInBackendObject, arrIndexInBackendObject: i })
          rows.current.push(row)
          rowsArrRefs.current.push(rowArrRefs)
        }
      }
    }
    setState({ data, isLoaded: true })
  }
  
  usePageHandler({ service, callback: handleData, allowBusy, allowErrorMsg, errorMessageFunc, busyOverride })
  return [arrRefs, state.data, state.isLoaded, rows.current, rowsArrRefs.current]
}

export default useFormPageHandler